<template>
  <div class="subscription-view">
    <v-container>
      <v-row>
        <v-col cols="12">
          <h1 class="text-h4 mb-4">Subscription</h1>
        </v-col>
      </v-row>

      <v-alert
        v-if="checkoutReturnPath"
        type="info"
        outlined
        class="mb-4 checkout-return"
      >
        Ready to go back?
        <router-link :to="checkoutReturnPath">Continue where you left off</router-link>
      </v-alert>

      <!-- Current Subscription Card -->
      <v-row>
        <v-col cols="12">
          <v-card class="mb-6" outlined>
            <v-card-title class="account-card-title">
              <v-icon left color="primary">mdi-card-account-details</v-icon>
              Current Subscription
            </v-card-title>
            <v-card-text v-if="loading">
              <v-skeleton-loader type="text@3" />
            </v-card-text>
            <v-card-text v-else-if="subscription || usage">
              <account-usage-summary v-if="usage" :usage="usage" @retry="refreshUsage"/>
              <v-chip v-if="usage && usage.planStatus" small outlined>{{ usage.planStatus }}</v-chip>
              <template v-if="!usage">
              <v-row>
                <v-col cols="12" md="4">
                  <div class="subtitle-1 font-weight-bold">Plan</div>
                  <div class="body-1">
                    {{ formatPlanName(subscription.subscription_plan) }}
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="subtitle-1 font-weight-bold">Status</div>
                  <div class="body-1">
                    <v-chip
                      :color="getStatusColor(subscription.subscription_status)"
                      small
                      text-color="white"
                    >
                      {{ formatStatus(subscription.subscription_status) }}
                    </v-chip>
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="subtitle-1 font-weight-bold">Renewal Date</div>
                  <div class="body-1">
                    {{ formatDate(subscription.subscription_end_date) }}
                  </div>
                </v-col>
              </v-row>

              <v-alert
                v-if="isScheduledDowngradeToFree"
                class="mt-4 mb-0"
                border="left"
                colored-border
                dense
                type="warning"
                outlined
              >
                <template v-if="forcePaidPlans">Your plan ends on</template>
                <template v-else>Your subscription will downgrade to the Free plan on</template>
                {{ formatDate(subscription.subscription_end_date) }}. You will
                keep access to the
                {{ formatPlanName(subscription.subscription_plan) }} plan until
                then.
              </v-alert>

              <v-divider class="my-4"></v-divider>

              <!-- Show the real spendable balance for every tier, including
                   starter, referral, adjustment, and purchased credits. -->
              <v-row v-if="showCreditInfo">
                <v-col cols="12" md="4">
                  <div class="subtitle-1 font-weight-bold">Total Credits</div>
                  <div class="display-1 font-weight-bold">
                    {{ formatCredits(subscription.credits_balance) }}
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="subtitle-1 font-weight-bold">
                    {{
                      isFreePlanUser
                        ? "Included & bonus credits"
                        : "Subscription Credits"
                    }}
                  </div>
                  <div class="body-1">
                    {{ formatCredits(subscription.subscription_credits) }}
                  </div>
                  <div class="caption text--secondary">
                    {{ isFreePlanUser ? "No daily reset" : "Resets monthly" }}
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="subtitle-1 font-weight-bold">
                    Purchased Credits
                  </div>
                  <div class="body-1">
                    {{ formatCredits(subscription.purchased_credits) }}
                  </div>
                  <div class="caption text--secondary">
                    Not subject to monthly reset
                  </div>
                </v-col>
              </v-row>

              <v-row v-if="showCreditInfo">
                <v-col cols="12" md="6">
                  <div class="subtitle-1 font-weight-bold">Credits Used</div>
                  <div class="body-1">
                    {{ formatCredits(subscription.credits_used) }}
                  </div>
                </v-col>
              </v-row>

              <v-alert
                v-if="isFreePlanUser"
                type="info"
                text
                dense
                class="mt-2"
              >
                <div class="font-weight-medium">
                  You have {{ formatCredits(freeCreditsRemaining) }} credits
                  remaining.
                </div>
                <!-- A paid-only site has no free tier: its members just have no plan yet. -->
                <div v-if="forcePaidPlans" class="mt-1">
                  You don't have a plan yet.
                  <router-link to="/subscribe">Choose a plan</router-link> to
                  start.<template v-if="guaranteeInfo"> {{ guaranteeInfo.long }}</template>
                </div>
                <template v-else>
                  <div class="mt-1">
                    Free accounts receive {{ freeCredits.amount }} starter credits
                    once after email verification. They do not refresh.
                  </div>
                  <div class="mt-1">
                    Subscribe for a bigger monthly allowance and higher
                    limits{{ aiProcessing ? " on AI processing" : "" }}.
                    <router-link to="/subscribe">Choose a plan</router-link>.
                  </div>
                </template>
              </v-alert>
              <p class="caption text--secondary mt-2 mb-0">
                Credits are SaaS usage units for {{ $config.productName }}
                processing time.
                They are not money, stored value, a wallet balance, or
                transferable value, and cannot be withdrawn, transferred,
                resold, or redeemed for cash. Included monthly credits reset
                each billing cycle and do not roll over. Prices are in {{ ($config.currency || 'usd').toUpperCase() }}.
              </p>

              <v-divider class="my-4"></v-divider>

              <v-row v-if="subscription.plan_details">
                <v-col cols="12">
                  <div class="subtitle-1 font-weight-bold mb-2">
                    Plan Features
                  </div>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <tbody>
                        <tr v-if="isFreePlanUser && forcePaidPlans">
                          <td>Plan</td>
                          <td>None yet</td>
                        </tr>
                        <tr v-else-if="isFreePlanUser">
                          <td>One-time starter credits</td>
                          <td>
                            {{ freeCredits.amount }} after email verification
                          </td>
                        </tr>
                        <tr v-else>
                          <td>Credits per month</td>
                          <td>
                            {{ subscription.plan_details.credits_per_month }}
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-col>
              </v-row>

              </template>
              <v-row v-if="subscription" class="mt-4">
                <v-col cols="12" class="text-right">
                  <v-btn
                    v-if="
                      subscription.subscription_plan.toLowerCase() !== 'free'
                    "
                    color="primary"
                    outlined
                    :loading="portalLoading"
                    @click="openBillingPortal"
                    class="mr-2"
                  >
                    Manage Billing
                  </v-btn>
                  <v-btn
                    v-if="
                      subscription.subscription_status === 'active' &&
                      subscription.subscription_plan.toLowerCase() !== 'free'
                    "
                    color="error"
                    outlined
                    @click="confirmCancelSubscription"
                    class="mr-2"
                  >
                    Cancel Subscription
                  </v-btn>
                  <v-btn
                    v-if="subscription.subscription_status === 'canceling'"
                    color="success"
                    outlined
                    @click="confirmResumeSubscription"
                  >
                    Resume Subscription
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-text v-else>
              <p>No subscription information available.</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Invite Friends CTA (hidden where there's no referral programme) -->
      <v-row v-if="referralsEnabled">
        <v-col cols="12">
          <v-card class="invite-friends-cta" outlined>
            <v-card-text
              class="d-flex align-center flex-wrap"
              style="gap: 16px"
            >
              <v-icon color="orange" size="36">mdi-gift-outline</v-icon>
              <div class="flex-grow-1">
                <div class="text-subtitle-1 font-weight-bold">
                  Get free credits by inviting friends
                </div>
                <div class="text-body-2">
                  Earn {{ referralCreditsPerSide }} credits for each friend who
                  joins and verifies their email. They get
                  {{ referralCreditsPerSide }} credits too. Up to 5 friends
                  earn you credits.
                </div>
              </div>
              <v-btn color="orange" :to="{ name: 'InviteFriends' }">
                Invite friends
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Available Plans -->
      <v-row>
        <v-col cols="12">
          <h2 class="text-h5 mb-4">Available Plans</h2>
        </v-col>
      </v-row>

      <!-- What you can do with the product (frames the plan choice).
           Reference-app copy (agent/API access, "the whole job") describes
           the platform's job-processing shape, not necessarily this app -
           apps with PLAN_MARKETING already carry their real
           differentiation on the plan cards below. -->
      <v-row v-if="!hasPlanMarketing">
        <v-col cols="12">
          <v-card outlined class="plan-value-strip mb-4">
            <v-card-text>
              <div class="text-subtitle-1 font-weight-medium mb-3">
                <v-icon left color="primary"
                  >mdi-star-four-points-outline</v-icon
                >
                What you can do with {{ $config.productName }}
              </div>
              <v-row dense>
                <v-col
                  v-for="vp in valueProps"
                  :key="vp.title"
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <div class="d-flex align-start">
                    <v-icon small color="primary" class="mr-2 mt-1">{{
                      vp.icon
                    }}</v-icon>
                    <div>
                      <div class="body-2 font-weight-medium">
                        {{ vp.title }}
                      </div>
                      <div class="caption grey--text">{{ vp.desc }}</div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-col
          v-for="plan in plans"
          :key="plan.id"
          cols="12"
          md="6"
          :lg="planColLg"
        >
          <subscription-plan-card
            :plan="plan"
            :current-plan="
              subscription ? subscription.subscription_plan : 'Free'
            "
            :scheduled-plan="scheduledPlanName"
            :scheduled-plan-label="scheduledPlanLabel"
            @select-plan="askPlanChange"
            @free-plan-selected="askFreeDowngrade"
          />
        </v-col>
      </v-row>

      <!-- Full feature comparison. Hidden for apps that supply
           PLAN_MARKETING: the generic rows (concurrency, API access, pack
           rates) describe the reference app, and the marketing cards above
           already carry the real differentiation. -->
      <v-row v-if="!hasPlanMarketing" class="mt-6">
        <v-col cols="12">
          <h2 class="text-h5 mb-4">Compare all plans</h2>
          <plan-comparison-table :plans="plans" />
        </v-col>
      </v-row>

      <!-- Credit History: apps without credits have no ledger activity to list -->
      <v-row v-if="creditsEnabled" class="mt-6">
        <v-col cols="12">
          <h2 class="text-h5 mb-4">{{ usage ? usageHistoryTitle : 'Credit History' }}</h2>
        </v-col>
      </v-row>

      <v-row v-if="creditsEnabled">
        <v-col cols="12">
          <v-card outlined>
            <v-data-table
              :headers="creditHistoryHeaders"
              :items="creditHistory"
              :loading="loading"
              :items-per-page="10"
              :no-data-text="historyEmptyText"
              class="elevation-0"
            >
              <template v-slot:item.amount="{ item }">
                <span
                  :class="{
                    'green--text': item.amount > 0,
                    'red--text': item.amount < 0
                  }"
                >
                  {{ item.amount > 0 ? "+" : "" }}{{ item.amount }}
                </span>
              </template>
              <template v-slot:item.transaction_type="{ item }">
                <v-chip
                  small
                  :color="getTransactionTypeColor(item.transaction_type)"
                  text-color="white"
                >
                  {{ formatTransactionType(item.transaction_type) }}
                </v-chip>
              </template>
              <template v-slot:item.created_at="{ item }">
                {{ formatDateTime(item.created_at) }}
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-overlay :value="isPostCheckoutSyncing" opacity="0.92" z-index="2000">
      <div class="post-checkout-sync">
        <v-progress-circular
          indeterminate
          size="64"
          width="6"
          color="primary"
          class="mb-4"
        />
        <div
          class="
            post-checkout-sync__message
            text-h6
            font-weight-medium
            text-center
          "
        >
          {{ syncingMessage }}
        </div>
      </div>
    </v-overlay>

    <!-- Cancel Subscription Dialog -->
    <v-dialog v-model="cancelDialog" max-width="500">
      <v-card>
        <v-card-title>Cancel Subscription</v-card-title>
        <v-card-text>
          <p>
            Are you sure you want to cancel your subscription? You will still
            have access to your current plan until the end of the billing
            period.
          </p>
          <p v-if="guaranteeInfo && guaranteeInfo.cancel">
            {{ guaranteeInfo.cancel }}
          </p>
          <p class="mb-1" style="font-weight: 600">
            Mind telling us why? (optional)
          </p>
          <v-radio-group v-model="cancelReason" dense hide-details class="mt-0">
            <v-radio
              v-for="r in cancelReasons"
              :key="r"
              :label="r"
              :value="r"
            ></v-radio>
          </v-radio-group>
          <v-textarea
            v-model="cancelComment"
            label="Anything else? (optional)"
            rows="2"
            auto-grow
            outlined
            dense
            class="mt-3"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDialog = false">No, Keep It</v-btn>
          <v-btn color="error" text @click="cancelSubscription"
            >Yes, Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Resume Subscription Dialog -->
    <v-dialog v-model="resumeDialog" max-width="500">
      <v-card>
        <v-card-title>Resume Subscription</v-card-title>
        <v-card-text>
          Would you like to resume your subscription? Your subscription will
          continue and you won't lose access to your plan at the end of the
          billing period.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="resumeDialog = false">No, Cancel</v-btn>
          <v-btn color="success" text @click="resumeSubscription"
            >Yes, Resume</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Plan change confirmation dialog -->
    <v-dialog v-model="confirmDialog" max-width="460" persistent>
      <v-card>
        <v-card-title class="text-h6">{{ confirmTitle }}</v-card-title>
        <v-card-text class="text-body-2" style="white-space: pre-line">{{
          confirmText
        }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="busy" @click="confirmDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="primary" :loading="busy" @click="runConfirmedAction">{{
            confirmCta
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success / info toast (green) -->
    <v-snackbar v-model="snackbar" color="success" :timeout="3500">{{
      snackbarText
    }}</v-snackbar>

    <!-- Error Snackbar -->
    <v-snackbar v-model="errorSnackbar" color="error" :timeout="5000">
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="errorSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { formatPlanPrice } from "@shared/utils/planPricing";
import { mapGetters, mapActions } from "vuex";
import { guarantee, checkoutNote, downgradeNote } from "@shared/utils/guarantee";
import SubscriptionPlanCard from "@shared/components/SubscriptionPlanCard.vue";
import PlanComparisonTable from "@shared/components/PlanComparisonTable.vue";
import api from "@shared/services/api";
import AccountUsageSummary from "@shared/components/AccountUsageSummary.vue";
import {accountUsage,refreshAccountUsage} from "@shared/utils/accountUsage";
import { formatPlanName } from "@shared/utils/planLabels";
import { safeInternalPath } from "@shared/utils/safePath";
import { isMember, registerFirst, serverMessage } from "@shared/utils/memberFunnel";
import { aiProcessing, creditsEnabled, usageHistoryTitle, historyEmptyText } from "@shared/utils/productWording";

export default {
  name: "SubscriptionView",
  components: {
    AccountUsageSummary,
    SubscriptionPlanCard,
    PlanComparisonTable
  },
  data() {
    return {
      cancelDialog: false,
      cancelReason: "",
      cancelComment: "",
      cancelReasons: [
        "Too expensive",
        "Not using it enough",
        "Missing features I need",
        "Found a better alternative",
        "Just testing / temporary",
        "Other"
      ],
      resumeDialog: false,
      portalLoading: false,
      selectedPlan: null,
      errorSnackbar: false,
      errorMessage: "",
      snackbar: false,
      snackbarText: "",
      confirmDialog: false,
      confirmTitle: "",
      confirmText: "",
      confirmCta: "Confirm",
      pendingAction: null,
      busy: false,
      isPostCheckoutSyncing: false,
      syncingMessage: "",
      checkoutReturnPath: "",
      creditHistoryHeaders: [
        { text: "Date", value: "created_at" },
        { text: "Amount", value: "amount" },
        { text: "Type", value: "transaction_type" },
        { text: "Description", value: "description" }
      ],
      valueProps: [
        {
          icon: "mdi-creation",
          title: "Input to finished result",
          desc: "Upload your file, get a finished result"
        },
        {
          icon: "mdi-account-star-outline",
          title: "Consistent, every time",
          desc: "The same quality carried through the whole job"
        },
        {
          icon: "mdi-timer-outline",
          title: "Runs automatically",
          desc: "Submit a job and it processes without manual steps"
        },
        {
          icon: "mdi-movie-open-outline",
          title: "Tailored to your input",
          desc: "Output built for your specific job, not a preset"
        },
        {
          icon: "mdi-check-decagram-outline",
          title: "Automatic quality checks",
          desc: "Flawed output is found and regenerated"
        },
        {
          icon: "mdi-robot-outline",
          title: "Agent and API access",
          desc: "Drive it from Claude Code, Codex or the API"
        }
      ]
    };
  },
  computed: {
    usage(){return accountUsage(this.$store,this.$config);},
    aiProcessing() { return aiProcessing(); },
    creditsEnabled() { return creditsEnabled(); },
    usageHistoryTitle() { return usageHistoryTitle(); },
    historyEmptyText() { return historyEmptyText(this.usage); },
    ...mapGetters([
      "currentSubscription",
      "availablePlans",
      "purchasablePlans",
      "forcePaidPlans",
      "currentUser",
      "creditHistory",
      "loading",
      "error",
      "isFreePlanUser",
      "showCreditInfo",
      "freeCredits",
      "freeCreditsRemaining"
    ]),
    subscription() {
      return this.currentSubscription;
    },
    guaranteeInfo() {
      return guarantee();
    },
    isFreeAllowedOverride() {
      return !!(this.currentUser && this.currentUser.free_allowed_override);
    },
    plans() {
      // Under force-paid, hide the Free plan card (and its downgrade path) from
      // users who are not grandfathered. Grandfathered users (free_allowed_override)
      // keep the Free option; everyone can still cancel via the cancel flow.
      if (this.forcePaidPlans && !this.isFreeAllowedOverride) {
        return this.purchasablePlans;
      }
      return this.availablePlans;
    },
    hasPlanMarketing() {
      return !!(this.$config && this.$config.PLAN_MARKETING);
    },
    // Absent config => enabled, so apps that never opt out are unaffected.
    referralsEnabled() {
      return !(this.$config && this.$config.REFERRALS_ENABLED === false);
    },
    // Per-app payout ($config.REFERRAL_CREDITS_PER_SIDE) - must mirror the
    // backend's SITE_<SLUG>_REFERRAL_CREDITS_PER_SIDE; absent = shared default.
    referralCreditsPerSide() {
      return (this.$config && this.$config.REFERRAL_CREDITS_PER_SIDE) || 100;
    },
    // The plan boxes were designed 4-across (lg="3" = 25%). When the Free plan
    // is hidden we render fewer boxes, so widen each to fill the row instead of
    // leaving a left-aligned gap: 3 -> lg=4 (33%), 2 -> lg=6 (50%), 1 -> lg=12,
    // 4+ -> lg=3 (25%).
    planColLg() {
      const n = (this.plans && this.plans.length) || 0;
      if (n >= 4) return 3;
      if (n <= 1) return 12;
      return Math.floor(12 / n);
    },
    isScheduledDowngradeToFree() {
      return (
        !!this.subscription &&
        this.subscription.subscription_status === "canceling" &&
        this.subscription.subscription_plan &&
        this.subscription.subscription_plan.toLowerCase() !== "free"
      );
    },
    scheduledPlanName() {
      return this.isScheduledDowngradeToFree ? "Free" : "";
    },
    scheduledPlanLabel() {
      if (!this.isScheduledDowngradeToFree) {
        return "Scheduled Next";
      }

      const endDate = this.formatDate(this.subscription.subscription_end_date);
      return endDate === "N/A" ? "Scheduled Next" : `Scheduled for ${endDate}`;
    }
  },
  methods: {
    refreshUsage(){return refreshAccountUsage(this.$store,this.$config);},
    formatPlanPrice(plan) { return formatPlanPrice(plan, this.$config); },
    ...mapActions([
      "fetchSubscription",
      "fetchPlans",
      "fetchCreditHistory",
      "createCheckoutSession",
      "cancelSubscription",
      "resumeSubscription"
    ]),
    // Expose the planLimits formatter so the template can render "Full history"
    // / "X years" / "X days" consistently for max_data_range_days and
    // timeframe_limits.* values.
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    },
    formatStatus(status) {
      if (!status) return "Unknown";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    formatPlanName,
    formatTransactionType(type) {
      if (!type) return "Unknown";
      return type.charAt(0).toUpperCase() + type.slice(1);
    },
    formatCredits(value) {
      if (value === null || value === undefined) return "0";
      return parseFloat(Number(value).toFixed(2));
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    async fetchSubscriptionSnapshot() {
      const response = await api.getCurrentSubscription();
      return response.data;
    },
    hasSubscriptionChanged(previousSubscription, nextSubscription) {
      if (!previousSubscription || !nextSubscription) {
        return false;
      }

      return (
        previousSubscription.subscription_plan !==
          nextSubscription.subscription_plan ||
        previousSubscription.subscription_status !==
          nextSubscription.subscription_status ||
        previousSubscription.subscription_end_date !==
          nextSubscription.subscription_end_date
      );
    },
    clearCheckoutQueryParams() {
      sessionStorage.removeItem("pendingSubscriptionPlanName");
      const query = { ...this.$route.query };
      delete query.success;
      delete query.canceled;
      delete query.next;
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    },
    // Resolves to "confirmed" only when the pending plan is now the account's
    // plan. A status-only change ("changed") can precede the paid invoice that
    // grants the plan, so it never counts as an active upgrade.
    async syncPostCheckoutState() {
      if (this.isPostCheckoutSyncing) {
        return "pending";
      }

      this.isPostCheckoutSyncing = true;
      this.syncingMessage = "Processing your subscription update...";
      const expectedPlanName = sessionStorage.getItem(
        "pendingSubscriptionPlanName"
      );
      const initialSubscription = this.subscription
        ? { ...this.subscription }
        : null;
      const maxAttempts = 8;
      const retryDelayMs = 1500;

      try {
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          const updatedSubscription = await this.fetchSubscriptionSnapshot();
          const planMatches = this.isPendingPlan(
            expectedPlanName,
            updatedSubscription
          );

          if (
            planMatches ||
            this.hasSubscriptionChanged(
              initialSubscription,
              updatedSubscription
            )
          ) {
            await this.fetchSubscription();
            await this.fetchCreditHistory();
            this.toast("Your subscription has been successfully updated!");
            return planMatches ? "confirmed" : "changed";
          }

          if (attempt < maxAttempts - 1) {
            await this.sleep(retryDelayMs);
          }
        }

        this.toast(
          "Your subscription update is still processing. Refresh in a few seconds if needed."
        );
        return "pending";
      } catch (error) {
        console.error("Error syncing post-checkout subscription state:", error);
        this.toast(
          "Your checkout completed, but the subscription update is still syncing. Refresh in a few seconds if needed."
        );
        return "failed";
      } finally {
        this.syncingMessage = "";
        this.isPostCheckoutSyncing = false;
      }
    },
    isPendingPlan(expectedPlanName, subscription) {
      return !!(
        expectedPlanName &&
        subscription &&
        subscription.subscription_plan &&
        subscription.subscription_plan.toLowerCase() ===
          expectedPlanName.toLowerCase()
      );
    },
    // A status change can arrive before the invoice that switches the plan.
    async waitForPendingPlan(expectedPlanName) {
      for (let attempt = 0; attempt < 6; attempt += 1) {
        await this.sleep(1500);
        let snapshot = null;
        try {
          snapshot = await this.fetchSubscriptionSnapshot();
        } catch (error) {
          snapshot = null;
        }
        if (this.isPendingPlan(expectedPlanName, snapshot)) {
          await this.fetchSubscription();
          return "confirmed";
        }
      }
      return "pending";
    },
    // Optional ?next= return path: only an active plan returns automatically,
    // and only while the user is still here; otherwise offer a link back.
    returnAfterCheckout(next, confirmed) {
      if (confirmed && this.$route.path === "/subscription") {
        this.$router.replace(next).catch(() => {});
        return;
      }
      this.checkoutReturnPath = next;
    },
    async syncDowngradeToFreeState(initialSubscription, successMessage) {
      if (this.isPostCheckoutSyncing) {
        return;
      }

      this.isPostCheckoutSyncing = true;
      this.syncingMessage = "Scheduling your downgrade...";
      const initialPlanName =
        initialSubscription &&
        initialSubscription.subscription_plan &&
        initialSubscription.subscription_plan.toLowerCase();
      const maxAttempts = 8;
      const retryDelayMs = 1500;

      try {
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          const updatedSubscription = await this.fetchSubscriptionSnapshot();
          const updatedPlanName =
            updatedSubscription &&
            updatedSubscription.subscription_plan &&
            updatedSubscription.subscription_plan.toLowerCase();
          const downgradeScheduled =
            !!updatedSubscription &&
            ((updatedPlanName === "free" &&
              initialPlanName &&
              initialPlanName !== "free") ||
              (updatedSubscription.subscription_status === "canceling" &&
                updatedPlanName === initialPlanName &&
                this.hasSubscriptionChanged(
                  initialSubscription,
                  updatedSubscription
                )));

          if (downgradeScheduled) {
            await this.fetchSubscription();
            await this.fetchCreditHistory();
            this.toast(
              successMessage ||
                "Your subscription will downgrade to the Free plan at the end of the current billing period."
            );
            return;
          }

          if (attempt < maxAttempts - 1) {
            await this.sleep(retryDelayMs);
          }
        }

        await this.fetchSubscription();
        await this.fetchCreditHistory();
        this.toast(
          successMessage ||
            "Your downgrade request was submitted, but the updated subscription state is still syncing. Refresh in a few seconds if needed."
        );
      } catch (error) {
        console.error("Error syncing free-plan downgrade state:", error);
        this.toast(
          successMessage ||
            "Your downgrade request was submitted, but the updated subscription state is still syncing. Refresh in a few seconds if needed."
        );
      } finally {
        this.syncingMessage = "";
        this.isPostCheckoutSyncing = false;
      }
    },
    getStatusColor(status) {
      switch (status) {
        case "active":
          return "success";
        case "past_due":
          return "warning";
        case "canceled":
        case "canceling":
          return "error";
        default:
          return "grey";
      }
    },
    getTransactionTypeColor(type) {
      switch (type) {
        case "consumption":
          return "red";
        case "renewal":
          return "green";
        case "adjustment":
          return "blue";
        case "purchase":
          return "purple";
        default:
          return "grey";
      }
    },
    toast(text) {
      this.snackbarText = text;
      this.snackbar = true;
    },
    planPriceByName(name) {
      if (!name) return 0;
      var lower = String(name).toLowerCase();
      var plans = this.plans || [];
      var match = plans.find(function (p) {
        return (p.name || "").toLowerCase() === lower;
      });
      return match ? Number(match.price_monthly || 0) : 0;
    },
    hasActivePaid() {
      var s = this.subscription;
      return (
        !!s &&
        !!s.subscription_plan &&
        s.subscription_plan.toLowerCase() !== "free" &&
        ["active", "canceling"].indexOf(s.subscription_status || "") !== -1
      );
    },
    askPlanChange(plan) {
      // Anonymous-trial guests cannot pay: register first, then continue to
      // the plan page with this plan picked.
      if (!isMember(this.$store)) {
        this.$router.push(registerFirst("/subscribe", plan.name)).catch(() => {});
        return;
      }
      var planLabel = formatPlanName(plan.name || "");
      var price = this.formatPlanPrice(plan) + "/month";
      if (!this.hasActivePaid()) {
        this.confirmTitle = `Start the ${planLabel} plan`;
        this.confirmText = `You'll be taken to Stripe to start your ${planLabel} subscription at ${price}. ${checkoutNote()}`;
        this.confirmCta = "Continue to Stripe";
      } else if (
        Number(plan.price_monthly || 0) >
        this.planPriceByName(this.subscription.subscription_plan)
      ) {
        this.confirmTitle = `Upgrade to ${planLabel}?`;
        this.confirmText = `You'll be upgraded to ${planLabel} (${price}) immediately. We'll charge the difference for the days remaining in your current billing period to your existing payment method - you won't be double-charged.`;
        this.confirmCta = "Upgrade now";
      } else {
        this.confirmTitle = `Switch to ${planLabel}?`;
        this.confirmText = `You'll be switched to ${planLabel} (${price}) right away. Your next charge will be at the new lower price${downgradeNote()}`;
        this.confirmCta = "Switch now";
      }
      var self = this;
      this.pendingAction = function () {
        return self.selectPlan(plan);
      };
      this.confirmDialog = true;
    },
    askFreeDowngrade() {
      var endDate = this.subscription
        ? this.formatDate(this.subscription.subscription_end_date)
        : "N/A";
      var effective =
        endDate && endDate !== "N/A"
          ? endDate
          : "the end of the billing period";
      this.confirmTitle = "Switch to the Free plan?";
      this.confirmText = `Your paid plan will be cancelled at the end of your current billing period (${effective}), and you'll switch to the Free plan then. You keep your current plan until then.`;
      this.confirmCta = "Switch to Free";
      var self = this;
      this.pendingAction = function () {
        return self.selectFreePlan();
      };
      this.confirmDialog = true;
    },
    async runConfirmedAction() {
      var action = this.pendingAction;
      if (!action) {
        this.confirmDialog = false;
        return;
      }
      await action();
    },
    selectPlan(plan) {
      // Regular paid plans proceed directly to checkout
      this.selectedPlan = plan;
      this.proceedToCheckout();
    },

    async selectFreePlan() {
      // Handle Free plan downgrade
      const initialSubscription = this.subscription
        ? { ...this.subscription }
        : null;

      this.busy = true;
      try {
        const response = await this.$store.dispatch("downgradeToFreePlan");
        this.confirmDialog = false;
        await this.syncDowngradeToFreeState(
          initialSubscription,
          response.message
        );
      } catch (error) {
        console.error("Error downgrading to free plan:", error);
        this.errorMessage =
          "Failed to downgrade to the Free plan. Please try again.";
        this.errorSnackbar = true;
      } finally {
        this.busy = false;
      }
    },
    proceedToCheckout() {
      if (!this.selectedPlan) return;

      if (!isMember(this.$store)) {
        this.$router
          .push(registerFirst("/subscribe", this.selectedPlan.name))
          .catch(() => {});
        return;
      }

      // Create success and cancel URLs
      const successUrl = `${window.location.origin}/subscription?success=true`;
      const cancelUrl = `${window.location.origin}/subscription?canceled=true`;

      this.busy = true;
      return this.createCheckoutSession({
        planId: this.selectedPlan.id,
        billingCycle: "monthly", // Always use monthly billing
        successUrl,
        cancelUrl
      })
        .then(response => {
          // In-place plan change (server returns a message + the success_url):
          // no redirect, just toast and refresh local state.
          if (response.message) {
            this.confirmDialog = false;
            this.toast(response.message);
            this.fetchSubscription();
            this.fetchCreditHistory();
          } else {
            // Brand-new subscription: hosted Stripe Checkout.
            sessionStorage.setItem(
              "pendingSubscriptionPlanName",
              this.selectedPlan.name
            );
            window.location.href = response.checkout_url;
          }
        })
        .catch(error => {
          console.error("Error creating checkout session:", error);
          this.errorMessage = serverMessage(
            error,
            "Failed to create checkout session. Please try again."
          );
          this.errorSnackbar = true;
        })
        .finally(() => {
          this.busy = false;
        });
    },
    openBillingPortal() {
      this.portalLoading = true;
      api
        .getAccountPortalUrl()
        .then(res => {
          var data = res && res.data ? res.data : res;
          var url = data && data.url ? data.url : null;
          if (url) {
            window.location.href = url;
          } else {
            this.errorMessage = "Billing portal is unavailable right now.";
            this.errorSnackbar = true;
          }
        })
        .catch(error => {
          console.error("Error opening billing portal:", error);
          this.errorMessage = "Billing portal is unavailable right now.";
          this.errorSnackbar = true;
        })
        .finally(() => {
          this.portalLoading = false;
        });
    },
    confirmCancelSubscription() {
      this.cancelDialog = true;
    },
    cancelSubscription() {
      this.cancelDialog = false;

      // Record the churn reason (best-effort; never blocks the cancel itself).
      if (this.cancelReason || this.cancelComment) {
        api
          .submitCancellationFeedback(this.cancelReason, this.cancelComment)
          .catch(() => {});
      }

      this.$store
        .dispatch("cancelSubscription")
        .then(response => {
          // Show success message
          this.toast(
            response.message ||
              "Your subscription has been canceled and will end at the end of the billing period."
          );
          // Refresh subscription data
          this.fetchSubscription();
        })
        .catch(error => {
          console.error("Error canceling subscription:", error);
          // Check if this is a "No active subscription" error
          let errorDetail = "";
          if (
            error.response &&
            error.response.data &&
            error.response.data.detail
          ) {
            errorDetail = error.response.data.detail;
          }

          if (errorDetail === "No active subscription to cancel") {
            this.errorMessage =
              "You don't have an active subscription to cancel.";
          } else {
            this.errorMessage =
              "Failed to cancel subscription. Please try again.";
          }
          this.errorSnackbar = true;
        });
    },
    confirmResumeSubscription() {
      this.resumeDialog = true;
    },
    resumeSubscription() {
      this.resumeDialog = false;

      this.$store
        .dispatch("resumeSubscription")
        .then(response => {
          // Show success message
          this.toast(
            response.message ||
              "Your subscription has been resumed successfully."
          );
          // Refresh subscription data
          this.fetchSubscription();
        })
        .catch(error => {
          console.error("Error resuming subscription:", error);
          // Check if this is a "No subscription to resume" error
          let errorDetail = "";
          if (
            error.response &&
            error.response.data &&
            error.response.data.detail
          ) {
            errorDetail = error.response.data.detail;
          }

          if (errorDetail === "No subscription to resume") {
            this.errorMessage = "You don't have a subscription to resume.";
          } else {
            this.errorMessage =
              "Failed to resume subscription. Please try again.";
          }
          this.errorSnackbar = true;
        });
    },
    async checkUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      const next = safeInternalPath(urlParams.get("next"));
      if (urlParams.get("success") === "true") {
        const expectedPlanName = sessionStorage.getItem(
          "pendingSubscriptionPlanName"
        );
        let outcome = await this.syncPostCheckoutState();
        if (next && outcome === "changed" && expectedPlanName) {
          outcome = await this.waitForPendingPlan(expectedPlanName);
        }
        this.clearCheckoutQueryParams();
        if (next) this.returnAfterCheckout(next, outcome === "confirmed");
      } else if (urlParams.get("canceled") === "true") {
        this.toast(
          "Checkout was canceled. Your subscription remains unchanged."
        );
        this.clearCheckoutQueryParams();
        if (next) this.checkoutReturnPath = next;
      }
    }
  },
  async created() {
    // Fetch subscription data
    await this.fetchSubscription();
    this.fetchPlans().then(() => {
      console.log("Available plans:", this.plans);
    });
    this.fetchCreditHistory();

    // Check URL parameters after hosted checkout redirect
    await this.checkUrlParams();
  }
};
</script>

<style scoped>
.post-checkout-sync {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 320px;
}

.post-checkout-sync__message {
  color: #fff !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}

.subscription-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
}

.subscription-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px var(--shadow-xs);
}

.invite-friends-cta {
  border-left: 4px solid #fb923c !important;
  background: linear-gradient(
    90deg,
    rgba(251, 146, 60, 0.12) 0%,
    rgba(251, 146, 60, 0.04) 100%
  );
}
</style>
